import { useCallback, useSyncExternalStore } from "react";

/**
 * Light and dark themes, as in the prototype: `data-theme` on <html> overrides the system
 * preference, and the choice is kept in localStorage (`wp-theme`).
 */
export type Theme = "light" | "dark";
const KEY = "wp-theme";
const listeners = new Set<() => void>();

function systemTheme(): Theme {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function currentTheme(): Theme {
  const set = document.documentElement.dataset.theme;
  return set === "dark" || set === "light" ? set : systemTheme();
}

export function applyStoredTheme(): void {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "dark" || stored === "light") document.documentElement.dataset.theme = stored;
  } catch {
    // ignore
  }
}

export function setTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // ignore
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
  mq?.addEventListener?.("change", listener);
  return () => {
    listeners.delete(listener);
    mq?.removeEventListener?.("change", listener);
  };
}

export function useTheme(): [Theme, () => void] {
  const theme = useSyncExternalStore(subscribe, currentTheme, () => "light" as Theme);
  const toggle = useCallback(() => setTheme(currentTheme() === "dark" ? "light" : "dark"), []);
  return [theme, toggle];
}

/** The prototype's round theme button (`.thm`). */
export function themeGlyph(theme: Theme): string {
  return theme === "dark" ? "☀" : "☽";
}
